import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from "react";
import AiAssistant from "@shared/AiAssistant";
import IconButton from "@shared/v2/IconButton";
import TextInput from "@shared/v2/TextInput";
import { Popover } from "@shared/v2/Popover";
import {
  BracketsCurlySolidV6,
  FaceSmileRegularV6,
  ImageSolidV6,
  SparklesSolidV6,
  SpinnerSolidV6,
  XmarkSolidV6,
} from "@shared/v2/Icomoon";
import type { PlaceholderOption, SmsComposerInputProps } from "./types";
import useSmsAiSuggestions from "./useSmsAiSuggestions";

const EmojiPicker = lazy(() => import("emoji-picker-react"));

const ACCEPTED_IMAGE_MIME_TYPES = new Set(["image/jpeg", "image/jpg", "image/png", "image/gif"]);
const ACCEPTED_IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif"]);
const MAX_IMAGE_SIZE_BYTES = 1 * 1024 * 1024; // 1 Mebibyte

const DEFAULT_MAX_HEIGHT = 100;

const INVALID_IMAGE_TYPE_ERROR = "Only JPG, JPEG, PNG, and GIF files are allowed.";
const INVALID_IMAGE_SIZE_ERROR = "Image files must be smaller than 1MB.";

const isAcceptedImageFile = (file?: File) => {
  if (!file) return false;

  if (file.type && ACCEPTED_IMAGE_MIME_TYPES.has(file.type.toLowerCase())) {
    return true;
  }

  const extension = file.name.split(".").pop()?.toLowerCase();
  return Boolean(extension && ACCEPTED_IMAGE_EXTENSIONS.has(extension));
};

const SmsComposerInput = ({
  aiAssistantContactUuid,
  className = "",
  containerClassName = "",
  conversationOwnerPersonUuids,
  disabled,
  error,
  fileValue,
  id,
  imageUploadDisabled,
  maxLength = 1500,
  maxHeight = DEFAULT_MAX_HEIGHT,
  messages,
  onChange,
  onFileChange,
  placeholderOptions,
  showImageUpload = true,
  value,
  ...rest
}: SmsComposerInputProps) => {
  const [isScrolling, setIsScrolling] = useState(false);
  const [isPlaceholderPopoverOpen, setIsPlaceholderPopoverOpen] = useState(false);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [placeholderSearch, setPlaceholderSearch] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { loading, suggestions } = useSmsAiSuggestions({
    aiAssistantContactUuid,
    conversationOwnerPersonUuids,
    disabled,
    messages,
  });
  const [selectedSuggestion, setSelectedSuggestion] = useState<string | null>(null);

  const allowImageUpload = Boolean(onFileChange && showImageUpload && !imageUploadDisabled);

  const adjustHeight = () => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = "auto";
    input.style.height = `${Math.min(maxHeight, Math.max(36, input.scrollHeight))}px`;
    input.style.overflow = input.scrollHeight > maxHeight ? "auto" : "hidden";
    input.scrollTop = input.scrollHeight;
    setIsScrolling(input.scrollHeight > maxHeight);
  };

  useLayoutEffect(() => {
    adjustHeight();
  }, [value, maxHeight]);

  useEffect(() => {
    if (!fileValue) {
      setPreviewUrl(null);
      return () => {};
    }
    setFileError(null);

    const objectUrl = URL.createObjectURL(fileValue);
    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [fileValue]);

  const validateFile = (file?: File) => {
    if (!file) return false;

    if (!isAcceptedImageFile(file)) {
      setFileError(INVALID_IMAGE_TYPE_ERROR);
      return false;
    }

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setFileError(INVALID_IMAGE_SIZE_ERROR);
      return false;
    }

    setFileError(null);
    return true;
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!allowImageUpload) return;
    e.currentTarget.style.outline = "2px dashed #999999";
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!allowImageUpload) return;
    e.currentTarget.style.outline = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.style.outline = "";
    if (!allowImageUpload) return;

    const file = e.dataTransfer.files?.[0];
    if (validateFile(file)) {
      onFileChange?.(file);
    }
  };

  const handleClearFile = () => {
    setFileError(null);
    onFileChange?.(null);
  };

  const insertPlaceholder = (opt: string | PlaceholderOption) => {
    const textarea = inputRef.current;
    if (!textarea) return;
    const { selectionStart, selectionEnd, value } = textarea;
    const newValue =
      value.substring(0, selectionStart ?? 0) +
      (typeof opt === "string" ? opt : opt.value) +
      value.substring(selectionEnd ?? value.length);
    onChange?.(newValue);
    setPlaceholderSearch("");
  };

  const filteredOptions: PlaceholderOption[] =
    placeholderOptions?.filter((opt) => opt.label.toLowerCase().includes(placeholderSearch.toLowerCase())) ??
    [];

  const textareaId = typeof id === "string" ? id : "text-message-textarea";

  return (
    <div
      className="tw-relative"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {fileValue && previewUrl && (
        <div className="tw-relative tw-inline-flex tw-mb-[8px]">
          <img src={previewUrl} className="tw-rounded-10px tw-max-h-[80px]" alt="MMS Preview" />
          {/* @ts-expect-error props */}
          <IconButton
            className="tw-absolute tw-top-[-10px] tw-right-[-10px]"
            schema="tertiary"
            size="small"
            onClick={handleClearFile}
          >
            <XmarkSolidV6 size="l" />
          </IconButton>
        </div>
      )}
      {!disabled && aiAssistantContactUuid && (
        <div className="tw-flex tw-justify-between tw-items-center tw-gap-[8px] tw-flex-wrap tw-whitespace-nowrap tw-mb-[8px]">
          <div className="tw-flex tw-items-center tw-gap-[4px]">
            {loading ? (
              <div className="tw-ml-[8px] tw-flex tw-gap-[4px] tw-items-center">
                <SpinnerSolidV6 className="tw-animate-spin" /> Writing some suggested responses...
              </div>
            ) : (
              suggestions?.categories.map((category) => (
                <button
                  className="tw-transition-colors tw-bg-white hover:tw-bg-[#ECE6FA] active:tw-bg-[#7532FA] hover:tw-text-[#572BAD] active:tw-text-white tw-px-[8px] tw-py-[4px] tw-rounded-4px tw-border-solid tw-border-[1px] tw-border-gray-10 hover:tw-border-[#D8C8FA] active:tw-border-[#7532FA] tw-inline-flex tw-items-center tw-gap-[4px]"
                  key={category.rank}
                  onMouseOver={() => {
                    setSelectedSuggestion(category.suggestions[0].text);
                  }}
                  onMouseLeave={() => {
                    setSelectedSuggestion(null);
                  }}
                  onClick={() => {
                    setSelectedSuggestion(null);
                    onChange?.(category.suggestions[0].text);
                    inputRef.current?.focus();
                  }}
                >
                  <SparklesSolidV6 size="s" />
                  {category.categoryDisplayName}
                </button>
              ))
            )}
          </div>
          <AiAssistant
            align="end"
            context={{}}
            isTinyMce={false}
            side="top"
            textareaId={textareaId}
            messageType="SMS"
            contactUuid={aiAssistantContactUuid}
            onInsertClick={onChange}
          />
        </div>
      )}
      <TextInput
        {...rest}
        // @ts-expect-error props
        id={id}
        value={selectedSuggestion || value}
        ref={inputRef}
        className={`tw-resize-none tw-pb-[32px] ${className}`}
        containerClassName={`tw-w-full ${containerClassName}`}
        error={fileError || error}
        maxLength={maxLength}
        multiline
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
          onChange?.(e.target.value);
          if (value === undefined) adjustHeight();
        }}
        rows={1}
        disabled={disabled}
      />
      <div
        className={`tw-absolute ${fileError || error ? "tw-bottom-[20px]" : "tw-bottom-0"} tw-right-0 ${isScrolling ? "tw-pr-[12px]" : "tw-pr-[8px]"} tw-pb-[4px] tw-flex tw-gap-[4px]`}
      >
        {placeholderOptions && (
          <Popover
            placement="top-end"
            maxHeight={300}
            open={isPlaceholderPopoverOpen}
            onOpenChange={setIsPlaceholderPopoverOpen}
          >
            {/* @ts-expect-error untyped JSX component */}
            <Popover.Trigger>
              {/* @ts-expect-error untyped JSX component */}
              <IconButton
                disabled={disabled}
                className={isPlaceholderPopoverOpen ? "!tw-text-black" : ""}
                schema="naked"
                size="small"
                onClick={() => {
                  setIsEmojiPickerOpen(false);
                  setIsPlaceholderPopoverOpen(!isPlaceholderPopoverOpen);
                }}
              >
                <BracketsCurlySolidV6 size="l" />
              </IconButton>
            </Popover.Trigger>
            {/* @ts-expect-error untyped JSX component */}
            <Popover.Content className="tw-min-w-[200px]">
              {filteredOptions.map((opt) => (
                // @ts-expect-error untyped JSX component
                <Popover.Item key={opt.label} onClick={() => insertPlaceholder(opt)}>
                  {opt.label}
                </Popover.Item>
              ))}
              <TextInput
                // @ts-expect-error props
                containerClassName="tw-sticky tw-bottom-0"
                value={placeholderSearch}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPlaceholderSearch(e.target.value)}
              />
            </Popover.Content>
          </Popover>
        )}
        <Popover placement="top-end" open={isEmojiPickerOpen} onOpenChange={setIsEmojiPickerOpen}>
          {/* @ts-expect-error untyped JSX component */}
          <Popover.Trigger>
            {/* @ts-expect-error untyped JSX component */}
            <IconButton
              className={isEmojiPickerOpen ? "!tw-text-black" : ""}
              size="small"
              schema="naked"
              onClick={() => {
                setIsPlaceholderPopoverOpen(false);
                setIsEmojiPickerOpen(!isEmojiPickerOpen);
              }}
              disabled={disabled}
            >
              <FaceSmileRegularV6 size="l" />
            </IconButton>
          </Popover.Trigger>
          {/* @ts-expect-error untyped JSX component */}
          <Popover.Content className="tw-rounded-[8px]">
            <Suspense fallback={<div className="tw-w-[350px] tw-h-[450px]" />}>
              <EmojiPicker
                open={isEmojiPickerOpen}
                autoFocusSearch
                onEmojiClick={({ emoji }) => {
                  setIsEmojiPickerOpen(false);
                  insertPlaceholder(emoji);
                }}
              />
            </Suspense>
          </Popover.Content>
        </Popover>
        {onFileChange && showImageUpload && (
          // @ts-expect-error untyped JSX component
          <IconButton
            schema="naked"
            size="small"
            onClick={() => {
              const fileInput = document.createElement("input");
              fileInput.type = "file";
              fileInput.accept = ".jpg,.jpeg,.png,.gif";
              fileInput.addEventListener("change", (e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (validateFile(file)) {
                  onFileChange(file ?? null);
                }
              });
              fileInput.click();
            }}
            disabled={imageUploadDisabled}
          >
            <ImageSolidV6 size="l" />
          </IconButton>
        )}
      </div>
    </div>
  );
};

SmsComposerInput.displayName = "SmsComposerInput";

export default SmsComposerInput;
